import { useState, useMemo } from 'react';
import MarkdownBlock from '../LazyMarkdownBlock';
import TagEditor from '../TagEditor';

export default function NoteDetail({ 
  selectedNote, 
  setSelectedNoteId, 
  handleCrystallizeNote, 
  handleReSummarize,
  isSummarizing,
  isCrystallizing, 
  selectedNoteInsightCount, 
  setSelectedInsightSourceNoteId, 
  setSelectedInsightType,
  setSelectedInsightStatus,
  setInsightSearchText,
  openSyncWorkspace, 
  handleUpdateNoteTags, 
  openRawSource,
  jumpToMap,
  handleDiscussInSink,
  insights = [],
  concepts = [],
  setSelectedInsightId,
  setSelectedAssetId,
  tagSuggestions = [],
  showToast,
}) {
  const [summaryCopied, setSummaryCopied] = useState(false);
  const [bodyCopied, setBodyCopied] = useState(false);

  const handleCopySummary = () => {
    navigator.clipboard.writeText(selectedNote?.summary || '');
    setSummaryCopied(true);
    setTimeout(() => setSummaryCopied(false), 2000);
  };

  const handleCopyBody = () => {
    navigator.clipboard.writeText(selectedNote?.body || '');
    setBodyCopied(true);
    setTimeout(() => setBodyCopied(false), 2000);
  };

  const [showAllInsights, setShowAllInsights] = useState(false);
  const [showAllAssets, setShowAllAssets] = useState(false);

  const relatedInsights = useMemo(() => {
    if (!selectedNote) return [];
    return insights.filter(i =>
      (i.sourceNoteIds || []).includes(selectedNote?.id) ||
      i.sourceNoteId === selectedNote?.id
    );
  }, [insights, selectedNote]);

  const relatedAssets = useMemo(() => {
    if (!selectedNote) return [];
    return concepts.filter(c => (c.sourceNoteIds || []).includes(selectedNote?.id));
  }, [concepts, selectedNote]);

  if (!selectedNote) return null;

  return (
    <div className="flex-1 min-h-0 overflow-y-auto overscroll-contain flex flex-col px-4 pb-4 space-y-4 relative">
      <div className="sticky top-0 z-10 bg-gray-900/95 backdrop-blur py-2 -mx-4 px-4 flex items-center justify-between gap-3 border-b border-gray-800/50">
        <button onClick={() => setSelectedNoteId(null)} className="text-sm text-blue-400 hover:text-blue-300 font-bold flex items-center gap-1">
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 19l-7-7 7-7" /></svg>
          一覧
        </button>
        <div className="flex items-center gap-1.5">
          {selectedNote?.metadata?.summaryCompleteness === 'partial' && (
            <button
              onClick={() => handleReSummarize?.(selectedNote)}
              disabled={isSummarizing}
              title={isSummarizing ? '再まとめ中' : '再まとめ'}
              className="p-2 rounded-lg border border-purple-700/50 bg-purple-950/40 text-purple-400 hover:bg-purple-600 hover:text-white hover:border-purple-500 disabled:opacity-40 transition-colors flex items-center justify-center"
            >
              {isSummarizing ? (
                <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
              ) : (
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                </svg>
              )}
            </button>
          )}
          <button
            onClick={handleCrystallizeNote}
            disabled={isCrystallizing}
            title={isCrystallizing ? '抽出中' : 'Hint抽出'}
            className="p-2 rounded-lg border border-purple-700/50 bg-purple-950/40 text-purple-400 hover:bg-purple-600 hover:text-white hover:border-purple-500 disabled:opacity-40 transition-colors"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 3v4M3 5h4M6 17v4m-2-2h4m5-16l2.286 6.857L21 12l-5.714 2.143L13 21l-2.286-6.857L5 12l5.714-2.143L13 3z" />
            </svg>
          </button>
          {selectedNoteInsightCount > 0 && (
            <button
              onClick={() => {
                setInsightSearchText?.('');
                setSelectedInsightType?.('all');
                setSelectedInsightStatus?.('all');
                setSelectedInsightSourceNoteId(selectedNote?.id);
                setSelectedInsightId?.(null);
                openSyncWorkspace('insights');
              }}
              title={`関連Hint ${selectedNoteInsightCount}件`}
              className="relative p-2 rounded-lg border border-gray-700/50 bg-gray-800/40 text-gray-400 hover:bg-gray-700 hover:text-white hover:border-gray-500 transition-colors"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
              </svg>
              <span className="absolute -top-1.5 -right-1.5 bg-gray-600 text-white text-[8px] font-black rounded-full min-w-[14px] h-[14px] flex items-center justify-center px-0.5">{selectedNoteInsightCount}</span>
            </button>
          )}
          {handleDiscussInSink && (
            <button
              onClick={() => handleDiscussInSink('note', selectedNote?.id, selectedNote?.title)}
              title="Sinkで議論"
              className="p-2 rounded-lg border border-indigo-700/50 bg-indigo-950/40 text-indigo-400 hover:bg-indigo-600 hover:text-white hover:border-indigo-500 transition-colors"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-5 5v-5z" />
              </svg>
            </button>
          )}
          {jumpToMap && (
            <button
              onClick={() => jumpToMap(`note:${selectedNote?.id}`, selectedNote?.title)}
              title="Mapで確認"
              className="p-2 rounded-lg border border-slate-700/50 bg-slate-900/40 text-slate-400 hover:bg-slate-700 hover:text-white hover:border-slate-500 transition-colors"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 20l-5.447-2.724A2 2 0 013 15.488V5.113a2 2 0 011.134-1.789L9 1m0 19l6 3m-6-3V1m6 22l5.447-2.724A2 2 0 0021 18.512V8.113a2 2 0 00-1.134-1.789L15 4m0 19V4m0 0L9 1" />
              </svg>
            </button>
          )}
        </div>
      </div>

      <article className="space-y-6">
        <header>
          <div className="flex items-center gap-2 mb-1.5">
            <span className="text-[10px] font-bold text-gray-500 uppercase tracking-widest bg-gray-800 px-1.5 py-0.5 rounded">Note</span>
            {/* Legacy: sourceMode === 'buddy' is kept for backward compatibility with old notes */}
            {selectedNote?.metadata?.sourceMode === 'buddy' && (
              <span className="text-[9px] font-black uppercase tracking-tighter text-indigo-300 bg-indigo-950/40 border border-indigo-800/50 rounded px-1.5 py-0.5">Buddy由来</span>
            )}
            {selectedNote?.metadata?.summaryCompleteness === 'partial' && (
              <span className="inline-flex items-center gap-1 text-[9px] font-black uppercase tracking-tighter text-amber-300 bg-amber-950/40 border border-amber-800/50 rounded px-1.5 py-0.5">
                <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" /></svg>
                まとめが途切れています
              </span>
            )}
          </div>
          <h2 className="text-xl font-bold text-white leading-tight">{selectedNote?.title || 'Untitled'}</h2>
          <div className="mt-3">
            <TagEditor
              tags={selectedNote?.tags || []}
              onUpdate={(newTags) => handleUpdateNoteTags(selectedNote?.id, newTags)}
              suggestions={tagSuggestions}
              showToast={showToast}
            />
          </div>
        </header>


        <section className="bg-gray-800/30 border border-gray-800/50 rounded-xl p-4 relative group/section">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-[10px] font-black uppercase tracking-widest text-gray-500">概要</h3>
            <button 
              onClick={handleCopySummary}
              className="text-[10px] font-bold text-gray-500 hover:text-gray-200 bg-gray-800/40 hover:bg-gray-800 border border-gray-700/50 px-2 py-0.5 rounded transition-all flex items-center gap-1 opacity-80 hover:opacity-100"
              title="概要をコピー"
            >
              {summaryCopied ? (
                <>
                  <svg className="w-3.5 h-3.5 text-emerald-400" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7"></path></svg>
                  <span className="text-emerald-400">コピーしました</span>
                </>
              ) : (
                <>
                  <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 5H6a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2v-1M8 5a2 2 0 002 2h2a2 2 0 002-2M8 5a2 2 0 012-2h2a2 2 0 012 2m0 0h2a2 2 0 012 2v3m2 4H10m0 0l3-3m-3 3l3 3"></path></svg>
                  <span>コピー</span>
                </>
              )}
            </button>
          </div>
          <div className="text-gray-300 text-[13px]">
            <MarkdownBlock text={selectedNote?.summary || ''} />
          </div>
        </section>

        <section className="bg-gray-800/30 border border-gray-800/50 rounded-xl p-4 relative group/section">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-[10px] font-black uppercase tracking-widest text-gray-500">詳細</h3>
            <button 
              onClick={handleCopyBody}
              className="text-[10px] font-bold text-gray-500 hover:text-gray-200 bg-gray-800/40 hover:bg-gray-800 border border-gray-700/50 px-2 py-0.5 rounded transition-all flex items-center gap-1 opacity-80 hover:opacity-100"
              title="詳細をコピー"
            >
              {bodyCopied ? (
                <>
                  <svg className="w-3.5 h-3.5 text-emerald-400" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7"></path></svg>
                  <span className="text-emerald-400">コピーしました</span>
                </>
              ) : (
                <>
                  <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 5H6a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2v-1M8 5a2 2 0 002 2h2a2 2 0 002-2M8 5a2 2 0 012-2h2a2 2 0 012 2m0 0h2a2 2 0 012 2v3m2 4H10m0 0l3-3m-3 3l3 3"></path></svg>
                  <span>コピー</span>
                </>
              )}
            </button>
          </div>
          <div className="text-gray-300 text-[13px]">
            <MarkdownBlock text={selectedNote?.body || ''} />
          </div>
        </section>

        {(relatedInsights.length > 0 || relatedAssets.length > 0) && (
          <section className="space-y-4 pt-2">
            <h3 className="text-[10px] font-black uppercase tracking-widest text-gray-500">関連知識</h3>

            {/* Related Hints */}
            {relatedInsights.length > 0 && (
              <div className="space-y-2">
                <div className="flex items-center gap-2 text-purple-400">
                  <span className="text-sm">💡</span>
                  <span className="text-[10px] font-bold uppercase tracking-wider">関連Hint ({relatedInsights.length})</span>
                </div>
                <div className="space-y-1.5">
                  {relatedInsights.slice(0, showAllInsights ? undefined : 5).map(insight => (
                    <button
                      key={insight.id}
                      onClick={() => { setSelectedInsightId(insight.id); openSyncWorkspace('insights'); }}
                      className="w-full flex items-center justify-between p-2 rounded-lg bg-gray-800/40 border border-gray-700/30 hover:bg-gray-800 hover:border-gray-600 transition-all group/item"
                    >
                      <span className="text-xs text-gray-300 truncate pr-4 group-hover/item:text-purple-300 transition-colors text-left">{insight.title}</span>
                      <svg className="w-3.5 h-3.5 text-gray-600 group-hover/item:text-purple-400 transition-colors shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5l7 7-7 7" /></svg>
                    </button>
                  ))}
                  {!showAllInsights && relatedInsights.length > 5 && (
                    <button
                      onClick={() => setShowAllInsights(true)}
                      className="text-[10px] font-bold text-gray-500 hover:text-gray-300 pl-2"
                    >
                      (+ {relatedInsights.length - 5} 件 もっと見る)
                    </button>
                  )}
                </div>
              </div>
            )}

            {/* Related Assets */}
            {relatedAssets.length > 0 && (
              <div className="space-y-2">
                <div className="flex items-center gap-2 text-emerald-400">
                  <span className="text-sm">📦</span>
                  <span className="text-[10px] font-bold uppercase tracking-wider">関連Asset ({relatedAssets.length})</span>
                </div>
                <div className="space-y-1.5">
                  {relatedAssets.slice(0, showAllAssets ? undefined : 3).map(asset => (
                    <button
                      key={asset.id}
                      onClick={() => { setSelectedAssetId(asset.id); openSyncWorkspace('assets'); }}
                      className="w-full flex items-center justify-between p-2 rounded-lg bg-gray-800/40 border border-gray-700/30 hover:bg-gray-800 hover:border-gray-600 transition-all group/item"
                    >
                      <div className="flex items-center gap-2 truncate pr-4">
                        <span className="text-xs text-gray-300 truncate group-hover/item:text-emerald-300 transition-colors text-left">{asset.title}</span>
                        <span className="text-[9px] text-gray-500 font-medium whitespace-nowrap">({asset.maturity || 'seed'})</span>
                      </div>
                      <svg className="w-3.5 h-3.5 text-gray-600 group-hover/item:text-emerald-400 transition-colors shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5l7 7-7 7" /></svg>
                    </button>
                  ))}
                  {!showAllAssets && relatedAssets.length > 3 && (
                    <button
                      onClick={() => setShowAllAssets(true)}
                      className="text-[10px] font-bold text-gray-500 hover:text-gray-300 pl-2"
                    >
                      (+ {relatedAssets.length - 3} 件 もっと見る)
                    </button>
                  )}
                </div>
              </div>
            )}
          </section>
        )}

        <button 
          onClick={() => openRawSource(selectedNote)} 
          disabled={!selectedNote?.sourceRoomIds?.length}
          className="w-full text-left bg-gray-800/40 border border-gray-700/50 hover:bg-gray-800 hover:border-gray-600 disabled:hover:bg-transparent disabled:opacity-30 rounded-xl p-4 transition-all group"
        >
          <div className="flex items-center justify-between">
            <p className="text-[10px] font-black uppercase tracking-widest text-gray-500">Sink（元メッセージ）を開く</p>
            <svg className="w-4 h-4 text-gray-600 group-hover:text-blue-400 transition-colors" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14"></path></svg>
          </div>
        </button>
      </article>
    </div>
  );
}
